import type { Province } from '../types/province';

/**
 * SF-167: shared three-ceiling max-explorable formula, extracted from `Explore.tsx` (which
 * computed it inline) and `MobileExploreShell.tsx`'s local `resolveBindingConstraint`, so
 * `RealmHub.tsx`'s hub-card stat can reuse the exact same computation instead of a second
 * hand-copy. Preserves `Explore.tsx`'s existing fallback constants (`|| 5`, `|| 750`) and its
 * `!province -> 0` behavior exactly.
 */
export interface ExploreCapacity {
  readonly maxBySoldiers: number;
  readonly maxByGold: number;
  readonly maxByPool: number;
  readonly maxExplorable: number;
}

export function computeExploreCapacity(province: Province | null): ExploreCapacity {
  const explorationCosts = province?.territory.explorationCosts;
  const soldierCostPerAcre = explorationCosts?.soldierCostPerAcre || 5;
  const goldCostPerAcre = explorationCosts?.goldCostPerAcre || 750;
  const availableSoldiers = province?.population.soldiers || 0;
  const availableGold = province?.resources.gold || 0;
  const maxByPool = province?.kingdom.explorePool || 0;

  const maxBySoldiers = Math.floor(availableSoldiers / soldierCostPerAcre);
  const maxByGold = Math.floor(availableGold / goldCostPerAcre);
  const maxExplorable = province ? Math.min(maxBySoldiers, maxByGold, maxByPool) : 0;

  return { maxBySoldiers, maxByGold, maxByPool, maxExplorable };
}

/** Ties resolve soldiers > gold > kingdom pool, deterministically — moved from MobileExploreShell.tsx unchanged. */
export function resolveBindingConstraint(maxBySoldiers: number, maxByGold: number, maxByPool: number): string {
  const min = Math.min(maxBySoldiers, maxByGold, maxByPool);
  if (maxBySoldiers === min) return 'soldiers';
  if (maxByGold === min) return 'gold';
  return 'kingdom pool';
}
